import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import type { CreateVisitorPassRequest } from '@condo/contracts';
import { CreateVisitorPassRequestSchema } from '@condo/contracts';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCondoContext } from '@/features/auth/use-condo-context';
import { useUnidades } from '@/features/unidades/api';

interface Props {
  onSubmit: (values: CreateVisitorPassRequest) => void;
  isSubmitting?: boolean;
  onCancel?: () => void;
}

export function VisitorPassForm({ onSubmit, isSubmitting = false, onCancel }: Props) {
  const { condoId } = useCondoContext();
  const { data: unidadesData, isLoading: loadingUnidades } = useUnidades(condoId);
  const unidades = unidadesData?.items ?? [];

  const form = useForm<CreateVisitorPassRequest>({
    resolver: zodResolver(CreateVisitorPassRequestSchema),
    defaultValues: {
      visitorNombre: '',
      visitorDni: '',
      vehiclePatent: '',
      unidadId: '',
    },
  });

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="visitorNombre">Nombre del visitante</Label>
          <Input
            id="visitorNombre"
            className="mt-1.5"
            placeholder="Juan Perez"
            {...form.register('visitorNombre')}
          />
          {form.formState.errors.visitorNombre && (
            <p className="mt-1 text-xs text-[var(--color-danger)]">
              {form.formState.errors.visitorNombre.message}
            </p>
          )}
        </div>
        <div>
          <Label htmlFor="visitorDni">RUT / DNI</Label>
          <Input
            id="visitorDni"
            className="mt-1.5"
            placeholder="12.345.678-9"
            {...form.register('visitorDni')}
          />
          {form.formState.errors.visitorDni && (
            <p className="mt-1 text-xs text-[var(--color-danger)]">
              {form.formState.errors.visitorDni.message}
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="vehiclePatent">Patente vehiculo (opcional)</Label>
          <Input
            id="vehiclePatent"
            className="mt-1.5"
            placeholder="ABCD-12"
            {...form.register('vehiclePatent')}
          />
          {form.formState.errors.vehiclePatent && (
            <p className="mt-1 text-xs text-[var(--color-danger)]">
              {form.formState.errors.vehiclePatent.message}
            </p>
          )}
        </div>
        <div>
          <Label>Unidad</Label>
          <Controller
            control={form.control}
            name="unidadId"
            render={({ field }) => (
              <Select
                value={field.value}
                onValueChange={field.onChange}
                disabled={loadingUnidades}
              >
                <SelectTrigger className="mt-1.5">
                  <SelectValue placeholder={loadingUnidades ? 'Cargando...' : 'Seleccionar unidad'} />
                </SelectTrigger>
                <SelectContent>
                  {unidades.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.numero}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
          {form.formState.errors.unidadId && (
            <p className="mt-1 text-xs text-[var(--color-danger)]">
              {form.formState.errors.unidadId.message}
            </p>
          )}
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 border-t border-[var(--color-hairline)] pt-4">
        {onCancel && (
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancelar
          </Button>
        )}
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Generando pase...
            </>
          ) : (
            'Crear pase de visita'
          )}
        </Button>
      </div>
    </form>
  );
}
